import { useAccount } from "@qw/auth";
import { isInternal } from "@qw/qw-common";
import { Button, FilePreviewDialog, Grid, GridCell, GridRowHeader, Icon, useFilePreviewDialog } from "@qw/ui";
import { type FC, type PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useInsuredFiles } from "../../hooks";
import {
  fileDisplayType,
  fileUploadDetails,
  groupInsuredFiles,
  isEmptyGroups,
  previewGroupHeadings,
  previewOrder,
} from "../../util";
import { FileLabelToggle } from "./file-label-toggle";
import { FileRow } from "./file-row";
import { FLAGGED_FILE_LABEL } from "./flagged-file-label";

export const FilesList = () => {
  const { user } = useAccount();
  const internal = isInternal(user);
  const { fileLabels, files, insuredId, loading, query, selectedFile, selectedTags, setSelectedFile, setSelectedTags } =
    useInsuredFiles();
  const [expandedYears, setExpandedYears] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    setExpandedYears(new Set());
  }, [insuredId]);

  // A search shows its matches everywhere, so more files open up while there's a query.
  const searching = query.trim().length > 0;
  const isExpanded = (year: string) => searching || expandedYears.has(year);
  const toggleYear = (year: string) =>
    setExpandedYears((curr) => {
      const next = new Set(curr);
      if (!next.delete(year)) {
        next.add(year);
      }
      return next;
    });

  const groups = useMemo(
    () => groupInsuredFiles(files, { internal, query, tag: selectedTags }),
    [files, internal, query, selectedTags]
  );
  const groupHeadings = useMemo(() => previewGroupHeadings(groups), [groups]);
  const filePreview = useFilePreviewDialog({
    files: previewOrder(groups, isExpanded),
    selectedFile,
    setSelectedFile,
  });

  const rows = (sectionFiles: typeof files) =>
    sectionFiles.map((file) => (
      <FileRow key={file.id} file={file} fileLabels={fileLabels} openFile={filePreview.openFile} />
    ));

  return (
    <div className="bg-card @3xl/files:h-full @3xl/files:overflow-y-auto">
      {groups.flagged.length > 0 && <GridWrapper header={FLAGGED_FILE_LABEL}>{rows(groups.flagged)}</GridWrapper>}
      {groups.years.map(({ year, keyDocuments, files: yearFiles, moreFiles }) => (
        <GridWrapper key={year} header={year}>
          {rows([...keyDocuments, ...yearFiles])}
          {moreFiles.length > 0 && !searching && (
            <div className="col-span-full py-2">
              <Button
                variant="ghost"
                size="sm"
                className="-ml-3 gap-2"
                aria-expanded={expandedYears.has(year)}
                onClick={() => toggleYear(year)}
              >
                <Icon icon={expandedYears.has(year) ? "keyboard_arrow_up" : "keyboard_arrow_down"} />
                More files ({moreFiles.length})
              </Button>
            </div>
          )}
          {isExpanded(year) && rows(moreFiles)}
        </GridWrapper>
      ))}
      {!loading && isEmptyGroups(groups) && (query || selectedTags) && (
        <div className="flex flex-col items-start gap-3 px-6 py-5 text-muted-foreground text-xs">
          <span>{selectedTags ? `No files in ${selectedTags} match` : "No files match"}</span>
          {selectedTags && query && (
            <Button variant="outline" size="sm" onClick={() => setSelectedTags("")}>
              Search all files
            </Button>
          )}
        </div>
      )}
      <FilePreviewDialog
        {...filePreview.dialogProps}
        getFileDetails={fileUploadDetails}
        getFileGroup={(file) => groupHeadings.get(file.id) ?? ""}
        getFileType={fileDisplayType}
        navButtons={
          filePreview.selectedFile && <FileLabelToggle file={filePreview.selectedFile} label={FLAGGED_FILE_LABEL} />
        }
      />
    </div>
  );
};

const GridWrapper: FC<PropsWithChildren & { header: string }> = ({ children, header }) => (
  <Grid className="shadow-border-b grid-cols-1 @[375px]/files:grid-cols-[auto_minmax(0,1fr)] @3xl/files:grid-cols-[8rem_4fr_1fr_3rem_3rem]">
    <GridRowHeader className="bg-card pt-5 space-y-3 sticky top-0 z-1">
      <h3 className="col-span-full text-foreground">{header}</h3>
      <GridCell className="@max-3xl/files:hidden">Type</GridCell>
      <GridCell className="@max-3xl/files:hidden">File Name</GridCell>
      <GridCell className="@max-3xl/files:hidden">Uploaded By</GridCell>
      <GridCell className="@max-3xl/files:hidden">Date</GridCell>
      <GridCell className="@max-3xl/files:hidden" />
    </GridRowHeader>
    {children}
  </Grid>
);
